import { motion } from "framer-motion";
import Image, { StaticImageData } from "next/image";

import { fadeIn, introSummary, services, textVariant } from "@/constants";
import styles from "@/ui/styles/shared.module.css";
import { Section } from "@/ui/organism/section";
import { cn } from "@/utils";

type ServiceCardProps = {
  index: number;
  title: string;
  content: string;
  icon: StaticImageData;
};

const missionHighlights = [
  {
    title: "Design DNA",
    body: "Brand systems meet creative code so every interface feels bespoke yet scalable.",
    meta: "Visual systems & storytelling",
  },
  {
    title: "Mission Method",
    body: "Strategy → prototypes → cinematic polish with fast feedback loops alongside your crew.",
    meta: "Collaborative sprints",
  },
  {
    title: "Impact Trail",
    body: "From cultural labs to startups, my launches consistently lift conversion and delight.",
    meta: "+NPS, +engagement",
  },
];

const capabilityTags = [
  "Creative Coding",
  "Design Systems",
  "Motion Direction",
  "3D / WebGL",
  "Brand Storytelling",
];

const ServiceCard = ({ index, title, content, icon }: ServiceCardProps) => {
  return (
    <motion.div
      className={cn(
        "w-full h-full overflow-hidden rounded-[20px] sm:rounded-[28px] border border-white/10 bg-white/5 my-16 sm:my-24 md:my-32 lg:my-44 py-3 px-[1px] backdrop-blur",
        styles.commonShell
      )}
    >
      <div className={styles.starfield} aria-hidden="true" />
      <div className={styles.nebula} aria-hidden="true" />
      <div className="flex h-full flex-col gap-4 sm:gap-5 rounded-[18px] sm:rounded-[26px] bg-gradient-to-b from-white/3 to-white/0.5 px-4 sm:px-6 py-6 sm:py-8">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-3 sm:gap-4">
            <span className="flex h-10 w-10 sm:h-12 sm:w-12 items-start justify-start rounded-xl sm:rounded-2xl bg-white/10 text-2xl sm:text-3xl md:text-4xl font-semibold text-white">
              0{index + 1}
            </span>
            <h3 className="text-base sm:text-lg font-semibold text-white">{title}</h3>
          </div>
          <div className="absolute top-6 right-6 sm:top-10 sm:right-10 h-20 w-20 sm:h-28 sm:w-28 md:h-32 md:w-32 overflow-hidden rounded-full p-2">
            <Image
              src={icon}
              alt={title}
              fill
              sizes="(max-width: 640px) 80px, 128px"
              className="object-contain"
            />
          </div>
        </div>
        <p className="text-base sm:text-lg md:text-xl text-white/70">{content}</p>
      </div>
    </motion.div>
  );
};

const ServiceStackScroll = () => {
  return (
    <div className="flex flex-col gap-y-32 sm:gap-y-48 md:gap-y-64 lg:gap-y-96 w-full max-w-[400px] md:max-w-[460px]">
      {services.map((service, index) => (
        <ServiceCard key={service.title} index={index} {...service} />
      ))}
    </div>
  );
};

const About = () => {
  return (
    <Section id="about" label="About" order={2}>
      <div className={cn(
        "rounded-[24px] sm:rounded-[32px] border bg-black/30 px-4 sm:px-6 md:px-8 py-6 sm:py-8 md:py-10 shadow-[0_25px_80px_rgba(5,7,19,0.65)] backdrop-blur-xl",
        styles.commonShell
      )}>
        <motion.div variants={textVariant()} className="space-y-3 sm:space-y-4">
          <p className="sectionSubText">Mission dossier</p>
          <h2 className="sectionHeadText">About the pilot</h2>
          <motion.p
            variants={fadeIn("", "", 0.1, 1)}
            className="paragraphText mt-4 max-w-4xl text-white/80 text-sm sm:text-base md:text-lg"
          >
            {introSummary}
          </motion.p>
        </motion.div>

        <div className="mt-6 sm:mt-8 flex flex-wrap gap-2 sm:gap-3">
          {capabilityTags.map((tag) => (
            <span
              key={tag}
              className="rounded-full border border-white/10 bg-white/5 px-3 sm:px-4 py-1.5 sm:py-2 text-[10px] sm:text-[11px] font-semibold uppercase tracking-[0.25em] text-white/70"
            >
              {tag}
            </span>
          ))}
        </div>
      </div>

			<div className="mt-10 sm:mt-12 md:mt-16 grid items-start gap-6 sm:gap-8 md:gap-10 xl:grid-cols-[minmax(0,1.1fr)_minmax(300px,0.8fr)]">
				<div className={cn(
          "w-full max-w-[640px] self-start rounded-[20px] sm:rounded-[28px] border border-white/10 bg-white/5 p-4 sm:p-6 md:p-8 backdrop-blur xl:sticky xl:top-[25vh]",
          styles.commonShell
        )}>
          <div className="space-y-6 sm:space-y-8">
            <div>
              <p className="text-[10px] sm:text-xs uppercase tracking-[0.4em] text-white/50">
                Operating principles
              </p>
              <h3 className="mt-2 sm:mt-3 text-xl sm:text-2xl md:text-3xl font-semibold text-white">
                Every mission blends art direction, front-end systems, and motion
                science.
              </h3>
              <p className="mt-3 sm:mt-4 text-sm sm:text-base text-white/70">
                I partner with product, brand, and research teams to translate
                ambitious ideas into tactile journeys—from narrative prototypes to
                launch-ready interactive campaigns.
              </p>
            </div>

            <div className="grid gap-3 sm:gap-4 grid-cols-1 md:grid-cols-2">
              {missionHighlights.map((highlight) => (
                <div
                  key={highlight.title}
                  className="relative overflow-hidden rounded-xl sm:rounded-2xl border border-white/10 p-3 sm:p-4"
                >
                  <div className={styles.starfield} aria-hidden="true" />
                  <div className={styles.nebula} aria-hidden="true" />
                  <p className="text-[10px] sm:text-[11px] uppercase tracking-[0.35em] text-white/50">
                    {highlight.meta}
                  </p>
                  <h4 className="mt-2 text-lg sm:text-xl font-semibold text-white">
                    {highlight.title}
                  </h4>
                  <p className="mt-2 text-xs sm:text-sm text-white/70">{highlight.body}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

				<div className="justify-self-center xl:justify-self-end">
          <ServiceStackScroll />
        </div>
      </div>
    </Section>
  );
};

export default About;
